import { evaluateSelector } from '@coast/schemas';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { Entity } from '../entity/Entity';
import { Field } from '../entity/fields/Field';
import { buildCardField } from './CardField';
import { isComponentHidden } from './utils';

interface CardEntityProps {
  entity: API.WorkflowEntity;
  workflowTemplateId: string;
  refs: NonNullable<NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs']>;
  viewTemplate?: API.ViewTemplateFragment;
  currentUser?: User;
}

export class CardEntity implements Entity {
  public readonly id: string;
  public readonly navigationViewTemplateId: string | null;
  // hidden fields removed
  public readonly fields: Map<string, Field | null>;
  // includes hidden fields
  public readonly allFields: Map<string, Field | null>;
  public readonly refs: NonNullable<NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs']>;
  public readonly workflowTemplateId: string;
  public readonly currentUser?: User;

  constructor({ entity, workflowTemplateId, refs, viewTemplate, currentUser }: CardEntityProps) {
    this.id = entity.id;
    this.refs = refs;
    this.fields = new Map();
    this.allFields = new Map();
    this.workflowTemplateId = workflowTemplateId;
    this.currentUser = currentUser;

    const workflowTemplate = refs.workflowTemplateRefs?.find(w => w.id === workflowTemplateId);
    this.navigationViewTemplateId = getNavigationViewTemplateId({ entity, workflowTemplate });

    workflowTemplate?.components.forEach(component => {
      const viewOptions = viewTemplate?.componentsViewOptions?.find(
        options => options.componentId === component.id,
      );
      if (!isComponentHidden(viewTemplate, viewOptions, component)) {
        this.fields.set(component.id, buildCardField({ entity, component, refs, currentUser }));
      }
      this.allFields.set(component.id, buildCardField({ entity, component, refs, currentUser }));
    });
  }
}

export function getNavigationViewTemplateId({
  entity,
  workflowTemplate,
}: {
  entity: Pick<API.WorkflowEntity, 'fields'>;
  workflowTemplate?: Pick<API.WorkflowTemplateFragment, 'behavior'> | null;
}): string | null {
  return (
    workflowTemplate?.behavior?.onOpenCard?.selectors.find(selector =>
      evaluateSelector(selector.selector, entity.fields ?? {}),
    )?.viewTemplateId ?? null
  );
}
